import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Stars } from "@/components/store/Stars";

export default function ReviewsAdmin() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api
      .get("/reviews/all")
      .then((r) => setReviews(r.data))
      .catch(() => toast.error("Gagal memuat ulasan"))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const toggle = async (r) => {
    try {
      await api.patch(`/reviews/${r.id}`, { published: !r.published });
      toast.success(r.published ? "Ulasan disembunyikan" : "Ulasan ditampilkan");
      load();
    } catch {
      toast.error("Gagal mengubah ulasan");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Hapus ulasan dari ${r.buyer_name}?`)) return;
    try {
      await api.delete(`/reviews/${r.id}`);
      toast.success("Ulasan dihapus");
      load();
    } catch {
      toast.error("Gagal menghapus ulasan");
    }
  };

  return (
    <div data-testid="reviews-admin">
      <div className="mb-8">
        <h1 className="font-serif-display text-3xl font-bold text-[#1E2B1D]">Ulasan Pelanggan</h1>
        <p className="text-xs text-[#6B7C66] mt-1">
          Ulasan tayang otomatis. Sembunyikan ulasan yang tidak pantas atau hapus permanen.
        </p>
      </div>

      {loading ? (
        <p data-testid="reviews-admin-loading" className="text-sm text-[#6B7C66]">Memuat ulasan...</p>
      ) : reviews.length === 0 ? (
        <p data-testid="reviews-admin-empty" className="text-sm text-[#6B7C66] bg-[#FBF9F5] rounded-2xl border border-[#E8E2D5] p-8 text-center">
          Belum ada ulasan masuk.
        </p>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div
              key={r.id}
              data-testid={`admin-review-${r.id}`}
              className={`bg-[#FBF9F5] rounded-2xl border p-5 flex flex-col sm:flex-row sm:items-start justify-between gap-4 ${r.published ? "border-[#E8E2D5]" : "border-dashed border-[#C5BCAB] opacity-70"}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <Stars value={r.rating} />
                  <span className="text-xs font-bold text-[#1E2B1D]">{r.buyer_name}</span>
                  <span className="font-mono text-[11px] text-[#3D5A40]">{r.order_code}</span>
                  <span
                    data-testid={`admin-review-status-${r.id}`}
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.published ? "bg-[#EAF2E9] text-[#3D5A40]" : "bg-[#F1E4E4] text-[#7A3A3A]"}`}
                  >
                    {r.published ? "Tayang" : "Disembunyikan"}
                  </span>
                </div>
                <p className="text-sm text-[#354634] mt-2 italic">“{r.comment}”</p>
                <p className="text-[11px] text-[#7E8F7A] mt-1">
                  {r.product_names?.join(", ")} · {new Date(r.created_at).toLocaleString("id-ID")}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => toggle(r)}
                  data-testid={`admin-review-toggle-${r.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#D5CCBC] text-xs font-semibold text-[#3D5A40] hover:bg-[#F4EFE6] transition-colors"
                >
                  {r.published ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  {r.published ? "Sembunyikan" : "Tampilkan"}
                </button>
                <button
                  onClick={() => remove(r)}
                  data-testid={`admin-review-delete-${r.id}`}
                  aria-label="Hapus ulasan"
                  className="p-2 rounded-lg border border-[#D5CCBC] text-red-600 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
